import { message } from 'antd';
import i18n from '~settings/i18n';
import { landlordActionTypes as actions, apiTokenActionTypes } from '~constants/actionTypes';
import { fetch } from '~actions/shared';
import { sortDirectionMapping, sortByMapping } from '~constants';
import endpoints from '~settings/endpoints';
import { formatSimpleParams } from '~helpers/params';
import { fetchAccountOwners } from '~actions/utilities';
import * as queries from '~settings/queries';

const setLandlords = payload => ({
  type: actions.SET_LANDLORDS,
  payload: payload.landlords,
});

export const getLandlords = params => dispatch => {
  fetch({
    dispatch,
    endpoint: endpoints.getLandlords.url(),
    params: queries.getLandlords(
      formatSimpleParams(
        {
          billingCity: null,
          billingCountry: null,
          statuses: null,
          registerTypes: null,
          slugs: null,
          pageNumber: 1,
          pageSize: 10,
          sortBy: sortByMapping.createdAt,
          sortDirection: sortDirectionMapping.descend,
        },
        params,
      ),
    ),
    communicationType: actions.GET_LANDLORDS_CS,
    successAction: setLandlords,
    onError: () => {
      message.error(i18n.t('cms.landlord.modal.toast.error'));
    },
  });
};

export const searchLandlordList =
  (successCallback = () => {}) =>
  dispatch => {
    fetch({
      dispatch,
      endpoint: endpoints.getLandlordList.url(),
      params: queries.searchLandlordList({
        pageNumber: 1,
        pageSize: 9999,
      }),
      communicationType: actions.LANDLORD_LIST_CS,
      onSuccess: res => {
        successCallback(res);
      },
    });
  };

export const setCurrentLandlord = payload => ({
  type: actions.SET_CURRENT_LANDLORD,
  payload,
});

export const getLandlord =
  (slug, successCallback = () => {}) =>
  dispatch => {
    fetch({
      dispatch,
      endpoint: endpoints.getLandlord.url(),
      params: queries.getLandlord(slug),
      communicationType: actions.GET_LANDLORD_CS,
      onSuccess: response => {
        successCallback(response.landlord);
      },
      onError: () => {
        message.error(i18n.t('cms.landlord.modal.toast.error'));
      },
    });
  };

export const setPreparedContract = payload => ({
  type: actions.SET_PREPARED_CONTRACT,
  payload,
});

export const createLandlord =
  (params, successCallback = () => {}) =>
  dispatch => {
    const formatParams = formatSimpleParams(
      {
        billingAddress: null,
        billingCity: null,
        billingCountry: null,
        billingPostalCode: null,
        invoicingFrequency: null,
        isLongtail: false,
        name: null,
        systemProvider: null,
        systemProviderComment: null,
        purchaseOrderRequired: null,
        apCategory: null,
        reconciliationPreference: null,
        reconciliationOption: null,
        reconciliationFrequency: null,
        bookingJourney: null,
        systemExternalId: null,
        companyType: null,
      },
      params,
    );

    fetch({
      dispatch,
      endpoint: endpoints.createLandlord.url(),
      params: queries.createLandlord(formatParams),
      communicationType: actions.CREATE_LANDLORD_CS,
      onSuccess: () => {
        successCallback();
      },
      onError: errors => {
        if (errors?.length > 0) {
          const code = errors[0]?.extensions.code;

          if (['ENTRATA_LANDLORD_NOT_FOUND', 'ENTRATA_LANDLORD_MAPPING_EXISTS'].includes(code)) {
            return message.error(i18n.t(`cms.landlord.modal.error.${code.toLowerCase()}`));
          }

          return message.error(i18n.t('cms.landlord.modal.toast.error'));
        }
      },
    });
  };

export const updateLandlord =
  (params, successCallback = () => {}, errorCallback = () => {}) =>
  dispatch => {
    const formatParams = formatSimpleParams(
      {
        allowNoPayment: false,
        billingAddress: null,
        billingCity: null,
        billingCountry: null,
        billingPostalCode: null,
        businessNumber: null,
        businessScale: null,
        invoicingFrequency: null,
        isLongtail: false,
        name: null,
        systemProvider: null,
        systemProviderComment: null,
        id: null,
        purchaseOrderRequired: null,
        apCategory: null,
        bookingAutoConfirm: false,
        reconciliationPreference: null,
        reconciliationOption: null,
        reconciliationFrequency: null,
        accountManager: null,
        depositTransferTime: null,
        rentTransferTime: null,
        bookingJourney: null,
        systemExternalId: null,
        firstName: null,
        lastName: null,
        billingCityId: null,
        governmentIssuedId: null,
        companyType: null,
        accountDifferentiation: null,
        listingPlan: null,
      },
      params,
    );

    fetch({
      dispatch,
      endpoint: endpoints.updateLandlord.url(),
      params: queries.updateLandlord(formatParams),
      communicationType: actions.UPDATE_LANDLORD_CS,
      onSuccess: response => {
        successCallback(response.updateLandlord);
      },
      onError: errors => {
        errorCallback();

        if (errors?.length > 0) {
          const code = errors[0]?.extensions.code;

          if (['ENTRATA_LANDLORD_NOT_FOUND', 'ENTRATA_LANDLORD_MAPPING_EXISTS'].includes(code)) {
            return message.error(i18n.t(`cms.landlord.modal.error.${code.toLowerCase()}`));
          }

          return message.error(i18n.t('cms.landlord.modal.toast.error'));
        }
      },
    });
  };

export const setPropertiesOfLandlord = payload => ({
  type: actions.SET_PROPERTIES_OF_LANDLORD,
  payload,
});

export const getPropertiesByLandlordSlug = params => dispatch => {
  fetch({
    dispatch,
    endpoint: endpoints.getPropertiesByLandlordSlug.url(),
    params: queries.getPropertiesByLandlordSlug(
      formatSimpleParams(
        {
          pageNumber: 1,
          pageSize: 10,
          citySlug: null,
          countrySlug: null,
          landlordSlug: null,
          statuses: null,
          slugs: null,
          bookNowEnable: null,
          countrySlugs: null,
          bookingJourneys: null,
          externalSystems: null,
        },
        params,
      ),
    ),
    communicationType: actions.GET_PROPERTIES_BY_LANDLORD_SLUG_CS,
    successAction: setPropertiesOfLandlord,
    onError: () => {
      message.error(i18n.t('cms.landlord.modal.toast.error'));
    },
  });
};

export const searchPropertyNameList =
  (params, successCallback = () => {}) =>
  dispatch => {
    fetch({
      dispatch,
      endpoint: endpoints.searchPropertyNameList.url(),
      params: queries.searchPropertyNameList(params),
      communicationType: actions.SEARCH_PROPERTY_NAME_LIST_CS,
      onSuccess: res => {
        successCallback(res);
      },
    });
  };

export const getDisplayCountryList =
  (successCallback = () => {}) =>
  dispatch => {
    fetch({
      dispatch,
      endpoint: endpoints.getDisplayCountryList.url(),
      params: queries.getDisplayCountryList(),
      communicationType: actions.GET_DISPLAY_COUNTRY_LIST_CS,
      onSuccess: res => {
        successCallback(res);
      },
    });
  };

export const bindAutoConfirmSettings =
  (params, successCallback = () => {}) =>
  dispatch => {
    fetch({
      dispatch,
      endpoint: endpoints.defaultUrl.url(),
      params: queries.bindAutoConfirmSettings(params),
      communicationType: actions.BIND_AUTO_CONFIRM_SETTINGS,
      onSuccess: res => {
        successCallback(res);
      },
      onError: () => {
        message.error(i18n.t('cms.landlord.modal.toast.error'));
      },
    });
  };

export const getAccountOwners =
  (params, successCallback = () => {}) =>
  dispatch => {
    fetchAccountOwners(actions, params, successCallback, dispatch);
  };

export const createAPIToken =
  (params, successCallback = () => {}) =>
  dispatch => {
    fetch({
      dispatch,
      endpoint: endpoints.defaultUrl.url(),
      params: queries.mutationCreateApiToken(params),
      communicationType: apiTokenActionTypes.CREATE_API_TOKEN,
      onSuccess: res => {
        successCallback(res);
      },
    });
  };

export const queryGetApiTokens =
  (params, successCallback = () => {}) =>
  dispatch => {
    fetch({
      dispatch,
      endpoint: endpoints.defaultUrl.url(),
      params: queries.queryGetApiTokens(params),
      communicationType: apiTokenActionTypes.GET_API_TOKENS,
      onSuccess: res => {
        successCallback(res);
      },
    });
  };

export const updateApiToken =
  (params, successCallback = () => {}) =>
  dispatch => {
    fetch({
      dispatch,
      endpoint: endpoints.defaultUrl.url(),
      params: queries.mutationUpdateApiToken(params),
      communicationType: apiTokenActionTypes.UPDATE_API_TOKEN,
      onSuccess: res => {
        successCallback(res);
      },
    });
  };
